import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface DataSource {
  id: string;
  name: string;
  type: string;
  createdAt: string;
}

export interface SyncResult {
  discovered: number;
  inserted: number;
  updated: number;
}

export interface AccelerateResult {
  materializationId: string;
  clickhouseTable: string;
  rowsIngested: number;
  durationMs: number;
}

export function useDataSources() {
  const api = useApi();
  return useQuery<DataSource[]>({
    queryKey: ['data-sources'],
    queryFn: () => api.fetch('/data-sources'),
  });
}

export function useRegisterDataSource() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; type: 'canopy_mysql_proc' }) =>
      api.fetch<{ id: string; name: string }>('/data-sources', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['data-sources'] });
    },
  });
}

export function useSyncCatalog() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dataSourceId: string) =>
      api.fetch<SyncResult>(`/data-sources/${dataSourceId}/sync-catalog`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['datasets'] });
      void qc.invalidateQueries({ queryKey: ['data-sources'] });
    },
  });
}

export function useAccelerateDataset() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { datasetId: string; sampleParams: Record<string, unknown> }) =>
      api.fetch<AccelerateResult>(`/datasets/${input.datasetId}/accelerate`, {
        method: 'POST',
        body: JSON.stringify({ sampleParams: input.sampleParams }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['datasets'] });
    },
  });
}
